// Based off Andarist's react-textarea-autosize, licensed under MIT License
// I'm sure we can skip some of the stuff being done but mostly just going to
// keep it in line with what's there.

// We're going to be referencing lines from the original,
// the commit ref is 5953e024310ab0b6ad1c26b2bc36addb9cc23afa

// The one assumption we've made here is that the styling don't change, or at
// least anything that would affect into our measurements (box size, fonts etc)

import { type Ref, watch } from "vue";

// src/getSizingData.ts#L4
const SIZING_STYLES = [
  "borderBottomWidth",
  "borderLeftWidth",
  "borderRightWidth",
  "borderTopWidth",
  "boxSizing",
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "letterSpacing",
  "lineHeight",
  "paddingBottom",
  "paddingLeft",
  "paddingRight",
  "paddingTop",
  // non-standard
  "tabSize",
  "textIndent",
  // non-standard
  "textRendering",
  "textTransform",
  "width",
  "wordBreak",
];

// src/forceHiddenStyles.ts#L1
// I don't think we need to slap !important on these.
const hiddenStyles = {
  minHeight: "0",
  maxHeight: "none",
  height: "0",
  visibility: "hidden",
  overflow: "hidden",
  position: "absolute",
  top: "0",
  right: "0",
  zIndex: "-1000",
};

interface AutosizeOptions {
  minRows?: number;
  maxRows?: number;
}

const noop = () => {};

export const useTextareaAutosize = (
  ref: Readonly<Ref<HTMLTextAreaElement | null | undefined>>,
  value: Readonly<Ref<string>> | (() => string),
  { maxRows, minRows }: AutosizeOptions = {}
) => {
  watch(ref, (node, _previous, cleanup) => {
    if (!node) {
      return;
    }

    let resync: (value: string) => void = noop;

    const mimic = document.createElement("textarea");
    mimic.tabIndex = -1;
    mimic.ariaHidden = "true";

    // src/calculateNodeHeight.ts#L37
    document.body.appendChild(mimic);

    const observer = new ResizeObserver(() => {
      const style = window.getComputedStyle(node);

      // src/calculateNodeHeight.ts#L43
      for (const prop of SIZING_STYLES) {
        // @ts-expect-error
        mimic.style[prop] = style[prop];
      }

      // src/calculateNodeHeight.ts#L48
      Object.assign(mimic.style, hiddenStyles);

      const paddingSize =
        parseFloat(style.paddingBottom) + parseFloat(style.paddingTop);
      const borderSize =
        parseFloat(style.borderBottomWidth) + parseFloat(style.borderTopWidth);

      const isBorderBox = style.boxSizing === "border-box";
      const padSize = isBorderBox ? borderSize : -paddingSize;

      if (maxRows !== undefined || minRows !== undefined) {
        // src/calculateNodeHeight.ts#L56
        mimic.value = "x";

        const rowHeight = mimic.scrollHeight - paddingSize;

        // src/calculateNodeHeight.ts#L60
        if (minRows !== undefined) {
          let minHeight = rowHeight * minRows;
          if (isBorderBox) {
            minHeight = minHeight + paddingSize + borderSize;
          }

          node.style.minHeight = minHeight + "px";
        }

        // src/calculateNodeHeight.ts#L66
        if (maxRows !== undefined) {
          let maxHeight = rowHeight * maxRows;
          if (isBorderBox) {
            maxHeight = maxHeight + paddingSize + borderSize;
          }

          node.style.maxHeight = maxHeight + "px";
        }
      }

      resync = (value) => {
        // src/calculateNodeHeight.ts#L50
        mimic.value = value;
        node.style.height = mimic.scrollHeight + padSize + "px";
      };

      resync(node.value);
    });

    observer.observe(node);

    const stop = watch(value, (text) => resync(text), { immediate: true });

    cleanup(() => {
      mimic.remove();
      observer.disconnect();
      stop();
    });
  });
};
